import GameContentTabs from '@/components/game/GameContentTabs';
import useGame from '@/hooks/useGame';
import useTrailers from '@/hooks/useTrailers';
import { getCroppedImageUrl } from '@/services/image-service';
import { useT, useLocale } from '@/i18n/useLanguage';
import {
	Box,
	Flex,
	Grid,
	Heading,
	SimpleGrid,
	Spinner,
	Text,
	Badge,
	HStack,
	Image,
} from '@chakra-ui/react';
import { useParams } from 'react-router';
import GameDetails from '../components/game/GameDetails';
import MetaCritic from '@/components/common/MetaCritic';
import { LuStar, LuClock } from 'react-icons/lu';

const GameDetailPage = () => {
	const { slug } = useParams();
	const { data: game, isLoading, error } = useGame(slug!);
	const { data: movies } = useTrailers(slug!);
	const t = useT();
	const locale = useLocale();
	const dateLocale = locale === 'sv' ? 'sv-SE' : 'en-US';

	if (isLoading)
		return (
			<Flex justify="center" align="center" minH="50vh">
				<Spinner size="xl" color="brand.400" />
			</Flex>
		);
	if (error || !game) throw error;

	return (
		<Box>
			<Box position="relative" minH={{ base: '280px', md: '360px' }} overflow="hidden">
				<Image
					src={getCroppedImageUrl(game.background_image)}
					alt=""
					position="absolute"
					inset={0}
					w="100%"
					h="100%"
					objectFit="cover"
					filter="blur(2px)"
					transform="scale(1.05)"
				/>
				<Box
					position="absolute"
					inset={0}
					bgGradient="to-t"
					gradientFrom="bg"
					gradientVia="blackAlpha.700"
					gradientTo="blackAlpha.500"
				/>
				<Box
					position="relative"
					maxW="1600px"
					mx="auto"
					px={{ base: 4, md: 10 }}
					pt={{ base: 16, md: 24 }}
					pb={8}
				>
					<HStack gap={3} mb={3} flexWrap="wrap">
						{game.genres?.slice(0, 3).map((g) => (
							<Badge key={g.id} colorPalette="purple" rounded="full" variant="solid">
								{g.name}
							</Badge>
						))}
					</HStack>
					<Heading
						as="h1"
						fontSize={{ base: '3xl', md: '5xl' }}
						fontWeight="900"
						letterSpacing="tight"
						mb={4}
						textShadow="0 2px 20px rgba(0,0,0,0.5)"
					>
						{game.name}
					</Heading>
					<HStack gap={4} flexWrap="wrap" fontSize="sm" color="whiteAlpha.900">
						{game.rating > 0 && (
							<HStack gap={1}>
								<LuStar size={16} fill="#fbbf24" color="#fbbf24" />
								<Text fontWeight="700">{game.rating.toFixed(1)}</Text>
								<Text opacity={0.7}>
									({game.ratings_count} {t('ratings')})
								</Text>
							</HStack>
						)}
						<MetaCritic score={game.metacritic} />
						{game.playtime > 0 && (
							<HStack gap={1}>
								<LuClock size={14} />
								<Text>
									{game.playtime}
									{t('hAvgPlaytime')}
								</Text>
							</HStack>
						)}
						{game.released && (
							<Text opacity={0.8}>
								{t('released')}{' '}
								{new Date(game.released).toLocaleDateString(dateLocale, {
									year: 'numeric',
									month: 'long',
									day: 'numeric',
								})}
							</Text>
						)}
					</HStack>
				</Box>
			</Box>

			<Box maxW="1600px" mx="auto" px={{ base: 4, md: 10 }} py={8}>
				<Grid templateColumns={{ base: '1fr', lg: '1.2fr 1fr' }} gap={10} mb={10}>
					<Box borderRadius="2xl" overflow="hidden" borderWidth="1px" borderColor="border">
						<Image
							src={getCroppedImageUrl(game.background_image)}
							alt={game.name}
							w="100%"
							h={{ base: '220px', md: '360px' }}
							objectFit="cover"
						/>
					</Box>
					<GameDetails game={game} />
				</Grid>

				<SimpleGrid columns={1} gap={8}>
					<GameContentTabs game={game} movies={movies} gameId={slug} />
				</SimpleGrid>
			</Box>
		</Box>
	);
};

export default GameDetailPage;
